import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faClock,
  faEnvelope,
  faRedo,
  faTimesCircle,
  faArchive,
  faChevronDown,
  faChevronUp,
  faPlus,
  faPalette,
  faRotate,
  faTriangleExclamation,
  type IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { Button, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useConnectGmailModal } from '../context/ConnectGmailModalContext';
import WorkItemDrawer from '../components/WorkItemDrawer';
import PageContainer from '../components/PageContainer';
import ColorLegend from '../components/ColorLegend';
import { workItemsApi, WorkItem, isOffToday, type Attention } from '../api/workItems';
import { getTodayCalendar, type TodayCalendarResponse } from '../api/todayCalendar';
import { sourceKeyLabel } from '../utils/sourceKeyLabel';
import { AppliedSourceRule, patchWorkItemsForAppliedSourceRule } from '../components/sourceTeachEligibility';
import { domainsApi, DomainConfig } from '../api/domains';
import { useTriageNavigation } from '../hooks/useTriageNavigation';
import { useDrawerNavigation } from '../hooks/useDrawerNavigation';
import { todayBoardPileLabel } from '../utils/drawerChromeCopy';
import { isSourceHomeItem, omitTodayFamilyMembers, releaseLocalFamilyOnSourceHomeDelete } from '../utils/sourceFamily';
import { useActiveSyncJobs } from '../hooks/useActiveSyncJobs';
import { getTodaySyncButtonState, type TodaySyncKind, type TodaySyncTone } from '../utils/todaySyncButton';
import { getDomainMeta, getWorkItemCardClasses } from '../utils/workItemStyling';
import type { TodayAreaOption } from '../utils/todayAreaRow';
import { usePageCache } from '../hooks/usePageCache';
import { useAuth } from '../auth/AuthContext';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import {
  calendarYmdInZone,
  effectiveUserTimeZone,
  extractYmdFromDue,
  ymdDayDiff,
} from '../utils/userTime';
import {
  clearTodaySessionDomainLens,
  effectiveTodayDomainFilter,
  readTodaySessionDomainLens,
  writeTodaySessionDomainLens,
} from '../utils/domainPreference';
import { MAX_VISIBLE_PER_BUCKET, TodayColumn, type BucketKey, type TodayColumnProps } from './TodayColumn';
import { TodayWatchStacks, type WatchStack } from './TodayWatchStacks';
import TodayCalendarStrip from './TodayCalendarStrip';
import TodayAreaRow, { inkVars } from './TodayAreaRow';

const WORK_ITEMS_PAGE_LIMIT = 200;
const WORK_ITEMS_MAX_PAGES = 25;
const BULK_ARCHIVE_LIMIT = 200;
const COLOR_LEGEND_SEEN_KEY = 'colorLegendSeen.v1';
const LAST_FOCUS_ID_KEY = 'lastFocusId.v1';
const LAST_FOCUS_ITEM_KEY = 'lastFocusItem.v1';
const WATCH_STREAMS_EXPANDED_KEY = 'todayWatchStreamsExpanded.v1';

type Bucketed = Record<BucketKey, WorkItem[]>;

const SYNC_BUTTON_TONE_CLASSES: Record<TodaySyncTone, string> = {
  warning: 'bg-amber-500/10 text-chip-warning border border-amber-500/20',
  connect: 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20 hover:bg-indigo-500',
  success: 'bg-emerald-500/10 text-chip-success border border-emerald-500/20',
  neutral: 'text-theme-secondary border border-theme',
};

const SYNC_BUTTON_ICONS: Record<TodaySyncKind, IconDefinition> = {
  attention: faTriangleExclamation,
  unknown: faEnvelope,
  checking: faEnvelope,
  connect: faEnvelope,
  syncing: faRotate,
  stale: faClock,
  never: faClock,
  synced: faCheckCircle,
};

type UpdatePatch = Partial<Pick<WorkItem, 'state' | 'needs_action' | 'due_date' | 'snoozed_until'>>;

const normalize = (s?: string | null) => (s ?? '').trim().toUpperCase();

// Parse a date field that is logically "date-only" (e.g. "2025-12-15" or an ISO string).
// We parse the YYYY-MM-DD portion into a *local* Date at midnight to avoid timezone shifts.
const parseDateOnlyLocal = (value?: string | null): Date | null => {
  if (!value) return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d, 0, 0, 0, 0);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

// Parse a date-time field (e.g. snooze_until) and compare against actual "now".
const parseDateTime = (value?: string | null): Date | null => {
  if (!value) return null;
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const addDays = (dt: Date, days: number) => {
  const d = new Date(dt);
  d.setDate(d.getDate() + days);
  return d;
};

const watchSourceLabel = sourceKeyLabel;

const watchStackKeyForItem = (item: WorkItem) => {
  const raw = item.source_key || item.company || item.source || 'other';
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'other';
};

function buildWatchStacks(items: WorkItem[]): WatchStack[] {
  const now = Date.now();
  const grouped = new Map<string, WorkItem[]>();

  for (const item of items) {
    const state = normalize(item.state);
    // C29-1 AC10: a held or undecided-high item is watch in any open state.
    const heldOff = isOffToday(item.safety) && (state === 'TRIAGE' || state === 'ACTIVE');
    if (!heldOff && (state !== 'TRIAGE' || item.attention !== 'watch')) continue;
    const snoozedUntil = parseDateTime(item.snoozed_until);
    if (snoozedUntil && snoozedUntil.getTime() > now) continue;

    const key = watchStackKeyForItem(item);
    const current = grouped.get(key) || [];
    current.push(item);
    grouped.set(key, current);
  }

  const activityTime = (item: WorkItem) =>
    parseDateTime(item.last_activity_at)?.getTime() ?? 0;

  return Array.from(grouped.entries())
    .map(([key, stackItems]) => ({
      key,
      label: watchSourceLabel(key),
      items: stackItems.sort((a, b) => activityTime(b) - activityTime(a)),
    }))
    .sort((a, b) => activityTime(b.items[0]) - activityTime(a.items[0]));
}

function bucketize(items: WorkItem[], todayYmd: string): Bucketed {
  const now = new Date();

  const buckets: Bucketed = { now: [], next: [], triage: [], waiting: [] };

  for (const item of items) {
    const state = normalize(item.state);

    // Hide completed/archived items from Today.
    if (state === 'DONE' || state === 'ARCHIVED') continue;

    // Respect snooze until *actual time* (not midnight).
    const snoozedUntil = parseDateTime(item.snoozed_until);
    if (snoozedUntil && snoozedUntil.getTime() > now.getTime()) continue;

    // C29-1 AC10: a held or undecided-high item stays off the board in any open state;
    // its watch stack shows it, marked.
    if (isOffToday(item.safety)) continue;

    // TRIAGE items always go to New.
    if (state === 'TRIAGE') {
      if (item.attention === 'watch' || item.attention === 'ignore') continue;
      buckets.triage.push(item);
      continue;
    }

    const dueYmd = extractYmdFromDue(item.due_date);

    if (dueYmd) {
      const delta = ymdDayDiff(dueYmd, todayYmd);
      // Today, tomorrow, or overdue → Now (matches prior end-of-tomorrow window for date-only)
      if (delta <= 1) {
        buckets.now.push(item);
        continue;
      }
      if (delta <= 7) {
        buckets.next.push(item);
        continue;
      }
      buckets.waiting.push(item);
      continue;
    }

    // No action date but needs action → Now
    if (item.needs_action) {
      buckets.now.push(item);
      continue;
    }

    // Everything else → Waiting
    buckets.waiting.push(item);
  }

  // Sorting rules (kept simple and predictable):
  // - Now/Next: earliest due first (no due -> bottom)
  // - Triage/Waiting: most recently active first
  const dueSort = (a: WorkItem, b: WorkItem) => {
    const ya = extractYmdFromDue(a.due_date);
    const yb = extractYmdFromDue(b.due_date);
    if (ya && yb && ya !== yb) return ya.localeCompare(yb);
    if (ya && !yb) return -1;
    if (!ya && yb) return 1;
    const da = parseDateOnlyLocal(a.due_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const db = parseDateOnlyLocal(b.due_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    if (da !== db) return da - db;
    const aa = parseDateTime(a.last_activity_at)?.getTime() ?? 0;
    const ab = parseDateTime(b.last_activity_at)?.getTime() ?? 0;
    return ab - aa;
  };

  const activitySort = (a: WorkItem, b: WorkItem) => {
    const aa = parseDateTime(a.last_activity_at)?.getTime() ?? 0;
    const ab = parseDateTime(b.last_activity_at)?.getTime() ?? 0;
    return ab - aa;
  };

  buckets.now.sort(dueSort);
  buckets.next.sort(dueSort);
  buckets.triage.sort(activitySort);
  buckets.waiting.sort(activitySort);

  return buckets;
}

function plainTodayFetchError(message: string): string {
  if (message === 'Network Error' || /timeout of \d+ms exceeded/i.test(message)) {
    return "Couldn't refresh Today. Try again.";
  }
  return message;
}

function BucketEmptyContent({
  bucket,
  gmailConnected,
  mailNeedsReconnect,
  onAddWork,
  onRefresh,
  onConnectMail,
  onOpenWorkbench,
}: {
  bucket: BucketKey;
  gmailConnected: boolean;
  mailNeedsReconnect: boolean;
  onAddWork: () => void;
  onRefresh: () => void;
  onConnectMail: () => void;
  onOpenWorkbench: () => void;
}) {
  const actionsClass = 'flex flex-col gap-2 mt-4 w-full max-w-[240px]';
  switch (bucket) {
    case 'now':
      return (
        <div
          className="flex flex-col items-center justify-center py-8 px-3 text-center"
          data-testid="today-column-empty-now"
        >
          <p className="text-theme-secondary text-sm font-medium">Nothing urgent right now</p>
          <p className="text-theme-muted text-xs mt-1 max-w-[18rem]">
            This column stays empty until something is due soon, needs action, or needs triage elsewhere.
          </p>
          <div className={actionsClass}>
            <Button variant="contained" color="primary" size="small" onClick={onAddWork} sx={{ textTransform: 'none' }}>
              Add work
            </Button>
            <Button variant="text" size="small" onClick={onRefresh} sx={{ color: 'var(--color-text-secondary)', textTransform: 'none' }}>
              Refresh
            </Button>
          </div>
        </div>
      );
    case 'next':
      return (
        <div
          className="flex flex-col items-center justify-center py-8 px-3 text-center"
          data-testid="today-column-empty-next"
        >
          <p className="text-theme-secondary text-sm font-medium">Nothing scheduled in the near window</p>
          <p className="text-theme-muted text-xs mt-1 max-w-[18rem]">
            Items with due dates in the next week land here once they leave Priority Now.
          </p>
          <div className={actionsClass}>
            <Button variant="contained" color="primary" size="small" onClick={onOpenWorkbench} sx={{ textTransform: 'none' }}>
              Open Workbench
            </Button>
            <Button variant="text" size="small" onClick={onAddWork} sx={{ color: 'var(--color-text-secondary)', textTransform: 'none' }}>
              Add work
            </Button>
          </div>
        </div>
      );
    case 'triage':
      return (
        <div
          className="flex flex-col items-center justify-center py-8 px-3 text-center"
          data-testid="today-column-empty-triage"
        >
          {mailNeedsReconnect ? (
            <>
              <p className="text-theme-secondary text-sm font-medium">Mail stopped syncing</p>
              <p className="text-theme-muted text-xs mt-1 max-w-[18rem]">
                Reconnect this mailbox to bring new mail in.
              </p>
              <div className={actionsClass}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={onConnectMail}
                  sx={{ textTransform: 'none' }}
                  data-testid="today-triage-reconnect"
                >
                  Reconnect
                </Button>
                <Button variant="text" size="small" onClick={onAddWork} sx={{ color: 'var(--color-text-secondary)', textTransform: 'none' }}>
                  Add work manually
                </Button>
              </div>
            </>
          ) : gmailConnected ? (
            <>
              <p className="text-theme-secondary text-sm font-medium">No items need triage</p>
              <p className="text-theme-muted text-xs mt-1 max-w-[18rem]">
                New mail and unclassified items appear here when they need a first decision.
              </p>
              <div className={actionsClass}>
                <Button variant="contained" color="primary" size="small" onClick={onRefresh} sx={{ textTransform: 'none' }}>
                  Refresh
                </Button>
                <Button variant="text" size="small" onClick={onAddWork} sx={{ color: 'var(--color-text-secondary)', textTransform: 'none' }}>
                  Add work manually
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-theme-secondary text-sm font-medium">Connect mail to surface triage</p>
              <p className="text-theme-muted text-xs mt-1 max-w-[18rem]">
                Gmail pulls candidates into Triage. You can still add items by hand anytime.
              </p>
              <div className={actionsClass}>
                <Button variant="contained" color="primary" size="small" onClick={onConnectMail} sx={{ textTransform: 'none' }}>
                  Connect Gmail
                </Button>
                <Button variant="text" size="small" onClick={onAddWork} sx={{ color: 'var(--color-text-secondary)', textTransform: 'none' }}>
                  Add work manually
                </Button>
              </div>
            </>
          )}
        </div>
      );
    case 'waiting':
      return (
        <div
          className="flex flex-col items-center justify-center py-8 px-3 text-center"
          data-testid="today-column-empty-waiting"
        >
          <p className="text-theme-secondary text-sm font-medium">Nothing deferred here yet</p>
          <p className="text-theme-muted text-xs mt-1 max-w-[18rem]">
            Far-out due dates, or items without an urgent due date and without Needs Action, stay in Later—this is not “all done,”
            just not in the front queue.
          </p>
          <p className="text-theme-muted text-[11px] mt-2 max-w-[18rem] leading-snug">
            Tip: set a due date or toggle Needs Action to surface work in Priority Now.
          </p>
          <div className={`${actionsClass} mt-4`}>
            <Button variant="outlined" size="small" onClick={onOpenWorkbench} sx={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)', textTransform: 'none' }}>
              Browse Workbench
            </Button>
          </div>
        </div>
      );
    default:
      return null;
  }
}

const MODE_LABEL_OVERRIDES: Record<string, string> = {
  job_hunt: 'Job Search',
  finance: 'Finance',
  parenthood: 'Family',
  business: 'Business',
  personal: 'Personal',
  health: 'Health',
  general: 'General',
};

interface TodayBundle {
  items: WorkItem[];
  domains: DomainConfig[];
  gmailConnected: boolean;
}

/**
 * usePageCache aborts the signal when Today unmounts, refreshes or switches user.
 * Check it before every request so an aborted load never starts another one.
 */
function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw new DOMException('Today load cancelled', 'AbortError');
}

async function fetchTodayBundle(signal: AbortSignal): Promise<TodayBundle> {
  const collected: WorkItem[] = [];
  let page = 1;
  let total = Number.POSITIVE_INFINITY;

  while (page <= WORK_ITEMS_MAX_PAGES && collected.length < total) {
    throwIfAborted(signal);
    const res = await workItemsApi.getWorkItems(
      {
        page,
        limit: WORK_ITEMS_PAGE_LIMIT,
        state: 'TRIAGE,ACTIVE',
        sort_by: 'last_activity_at',
        sort_order: 'desc',
      },
      { signal },
    );

    collected.push(...res.items);
    total = Number.isFinite(res.total) ? res.total : collected.length;

    if (res.items.length < WORK_ITEMS_PAGE_LIMIT) break;
    page += 1;
  }

  if (page > WORK_ITEMS_MAX_PAGES && collected.length < total) {
    console.warn(
      `TodayPage: loaded ${collected.length}/${total} items; reached safety cap of ${WORK_ITEMS_MAX_PAGES} pages.`
    );
  }

  const deduped = new Map<string, WorkItem>();
  for (const item of collected) {
    if (!deduped.has(item.id)) deduped.set(item.id, item);
  }

  // These two may fail without failing Today, but a cancel must still stop the load.
  let gmailConnected = false;
  throwIfAborted(signal);
  try {
    const me = await workItemsApi.getCurrentUser({ signal });
    gmailConnected = !!me.google_connected;
  } catch (e) {
    if (signal.aborted) throw e;
  }

  let domains: DomainConfig[] = [];
  throwIfAborted(signal);
  try {
    domains = await domainsApi.getDomains({ signal });
  } catch (e) {
    if (signal.aborted) throw e;
  }

  return { items: Array.from(deduped.values()), domains, gmailConnected };
}

const TodayPage: React.FC = () => {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  const navigate = useNavigate();
  // Same source of truth as the ambient sync indicator — the header chip must
  // never show green "Sync Active" while a mailbox needs attention (failed
  // last_sync_job or canonical sync_health).
  const mailboxSync = useActiveSyncJobs();
  const mailboxNeedsAttention = Boolean(mailboxSync.anyFailed || mailboxSync.anyGrantAttention);
  // Header attention includes retryable sync failures. Reconnect copy is grant-only.
  const mailGrantNeedsReconnect = Boolean(mailboxSync.anyGrantAttention);
  const { bootstrap, loading: bootstrapLoading } = useAppBootstrap();
  const userTimeZoneStored = bootstrap?.user?.timezone ?? null;
  const tzEffective = effectiveUserTimeZone(userTimeZoneStored);
  const todayYmd = calendarYmdInZone(new Date(), tzEffective);

  const {
    data: bundle,
    loading,
    error,
    refresh,
    setData: setBundle,
  } = usePageCache<TodayBundle>({
    key: userId != null ? `today:${userId}` : 'today',
    fetcher: fetchTodayBundle,
    staleMs: 60_000,
  });

  const [calendar, setCalendar] = useState<TodayCalendarResponse | null>(null);
  const calendarAbortRef = useRef<AbortController | null>(null);
  // True while the calendar effect below is live, so while Today is mounted.
  const calendarLiveRef = useRef(false);

  const loadCalendar = useCallback(() => {
    // A save that finishes after Today is left still calls this; it must not start a request.
    if (!calendarLiveRef.current) return;
    // One calendar request at a time: a newer load cancels the one in flight.
    calendarAbortRef.current?.abort();
    const controller = new AbortController();
    calendarAbortRef.current = controller;
    Promise.resolve()
      .then(() =>
        typeof getTodayCalendar === 'function'
          ? getTodayCalendar(tzEffective, { signal: controller.signal })
          : { state: 'not_connected' as const, events: [] },
      )
      .then((payload) => {
        if (controller.signal.aborted) return;
        const state =
          payload?.state === 'events' || payload?.state === 'empty'
            ? payload.state
            : 'not_connected';
        setCalendar({
          state,
          events: Array.isArray(payload?.events) ? payload.events : [],
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setCalendar({ state: 'not_connected', events: [] });
        }
      });
  }, [tzEffective]);

  useEffect(() => {
    calendarLiveRef.current = true;
    loadCalendar();
    // Leaving Today cancels the calendar request in flight, including one a refresh started,
    // and stops any later call from starting a new one.
    return () => {
      calendarLiveRef.current = false;
      calendarAbortRef.current?.abort();
    };
  }, [userId, loadCalendar]);

  const refreshToday = useCallback(() => {
    refresh();
    loadCalendar();
  }, [refresh, loadCalendar]);

  const items = useMemo(() => bundle?.items ?? [], [bundle?.items]);
  const todayBoardItems = useMemo(() => omitTodayFamilyMembers(items), [items]);
  const domains = useMemo(() => bundle?.domains ?? [], [bundle?.domains]);
  // Mailbox rows win over /user/me only after a successful GET /mailboxes.
  // A timeout sets loaded with an empty list and must not hide a connected account.
  const mailboxRowsKnown = mailboxSync.pollSucceeded === true;
  const gmailConnected = mailboxRowsKnown
    ? Boolean(mailboxSync.hasConnectedMailbox)
    : (bundle?.gmailConnected ?? false);
  const syncState = getTodaySyncButtonState({
    needsAttention: mailboxNeedsAttention,
    rowsKnown: mailboxRowsKnown,
    connected: gmailConnected,
    syncing: mailboxSync.anyActive,
    connectedSyncTimes: mailboxSync.connectedSyncTimes ?? [],
  });
  // Same breakpoint as Tailwind `lg`, the Sidebar and the phone top bar. Below it the sync
  // button joins the title row; it renders once at any width.
  const isDesktop = useMediaQuery('(min-width: 1024px)', { noSsr: true });

  const setItems = useCallback(
    (updater: WorkItem[] | ((prev: WorkItem[]) => WorkItem[])) => {
      setBundle((prev) => {
        if (!prev) return prev;
        const next = typeof updater === 'function' ? updater(prev.items) : updater;
        return { ...prev, items: next };
      });
    },
    [setBundle],
  );

  /** `null` = follow Profile/bootstrap; non-null = session lens (including Life View `all`) */
  const [sessionDomainLens, setSessionDomainLens] = useState<string | null>(() =>
    userId != null ? readTodaySessionDomainLens(userId) : null,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const focusIdFromUrl = searchParams.get('focusId');
  const deepLinkAppliedRef = useRef<string | null>(null);
  const drawerActivatorRef = useRef<HTMLElement | null>(null);
  const drawerSelectionRef = useRef(0);
  const [boardLoadAnnouncement, setBoardLoadAnnouncement] = useState('');
  const prevLoadingRef = useRef(false);

  const mailboxCallback = searchParams.get('mailbox');
  const { openModal: openConnectGmailModal } = useConnectGmailModal();
  const [drawerItem, setDrawerItem] = useState<WorkItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [expanded, setExpanded] = useState<Record<BucketKey, boolean>>({
    now: false,
    next: false,
    triage: false,
    waiting: false,
  });
  const [expandedWatchStacks, setExpandedWatchStacks] = useState<Set<string>>(
    () => new Set(),
  );
  const [archivingWatchStacks, setArchivingWatchStacks] = useState<Set<string>>(
    () => new Set(),
  );
  const [watchStackError, setWatchStackError] = useState<string | null>(null);
  const [legendOpen, setLegendOpen] = useState(false);
  const legendAutoOpenedRef = useRef(false);

  useEffect(() => {
    if (userId == null) {
      setSessionDomainLens(null);
      return;
    }
    setSessionDomainLens(readTodaySessionDomainLens(userId));
  }, [userId]);

  useEffect(() => {
    if (mailboxCallback) openConnectGmailModal();
  }, [mailboxCallback, openConnectGmailModal]);

  useEffect(() => {
    if (loading) return;
    if (legendAutoOpenedRef.current) return;
    legendAutoOpenedRef.current = true;
    try {
      const seen = localStorage.getItem(COLOR_LEGEND_SEEN_KEY) === '1';
      if (!seen) setLegendOpen(true);
    } catch {
      // Ignore localStorage failures.
    }
  }, [loading]);

  const closeLegend = () => {
    setLegendOpen(false);
    try {
      localStorage.setItem(COLOR_LEGEND_SEEN_KEY, '1');
    } catch {
      // Ignore localStorage failures.
    }
  };

  const filterDomain = useMemo(() => {
    const deferUntilBootstrap = !!session && bootstrapLoading && bootstrap === null;
    return effectiveTodayDomainFilter(
      sessionDomainLens,
      bootstrap?.active_domain_key ?? bootstrap?.user?.active_domain_key,
      { deferUntilBootstrap },
    );
  }, [session, sessionDomainLens, bootstrap, bootstrapLoading]);

  const domainBootstrapPending = filterDomain === null;
  const workItemsLoading = filterDomain !== null && loading;

  useEffect(() => {
    if (filterDomain === null) return;
    if (prevLoadingRef.current && !loading) {
      setBoardLoadAnnouncement('Today board loaded');
      const id = window.setTimeout(() => setBoardLoadAnnouncement(''), 1500);
      prevLoadingRef.current = loading;
      return () => clearTimeout(id);
    }
    prevLoadingRef.current = loading;
  }, [loading, filterDomain]);

  const setFilterDomain = useCallback(
    (id: string) => {
      setSessionDomainLens(id);
      if (userId != null) {
        writeTodaySessionDomainLens(userId, id);
      }
    },
    [userId],
  );

  const followProfileDomainLens = useCallback(() => {
    setSessionDomainLens(null);
    if (userId != null) {
      clearTodaySessionDomainLens(userId);
    }
  }, [userId]);

  const hasSessionLensOverride = sessionDomainLens !== null;

  const filteredItems = useMemo(() => {
    if (filterDomain === null) return [];
    if (filterDomain === 'all') return todayBoardItems;
    return todayBoardItems.filter(item => {
      const d = (item.domain || '').toLowerCase().trim();
      const f = filterDomain.toLowerCase().trim();
      // Broad matching for job_hunt
      if (f === 'job_hunt') {
        return d === 'job_hunt' || d === 'job search' || d === 'application' || d === 'applied';
      }
      return d === f;
    });
  }, [todayBoardItems, filterDomain]);

  const bucketed = useMemo(
    () => bucketize(filteredItems, todayYmd),
    [filteredItems, todayYmd],
  );
  const watchStacks = useMemo(
    () => buildWatchStacks(filteredItems),
    [filteredItems],
  );

  const userFirstName =
    (bootstrap?.user?.display_name || '').trim().split(/\s+/)[0] || null;
  const workItemStylingOptions = { userTimeZone: userTimeZoneStored, todayYmd };
  const headerDateLine = useMemo(() => {
    const parts = todayYmd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const anchor = parts
      ? new Date(Date.UTC(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]), 12, 0, 0, 0))
      : new Date();
    try {
      return new Intl.DateTimeFormat(undefined, {
        timeZone: tzEffective,
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }).format(anchor);
    } catch {
      return new Intl.DateTimeFormat(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }).format(anchor);
    }
  }, [tzEffective, todayYmd]);

  const contextOptions = useMemo((): TodayAreaOption[] => {
    const base = [
      { id: 'all', label: 'All areas' },
      { id: 'job_hunt', label: 'Job Hunt' },
      { id: 'finance', label: 'Finance' },
      { id: 'parenthood', label: 'Parenthood' },
      { id: 'business', label: 'Business' },
    ];

    const map = new Map<string, { id: string; label: string }>();
    for (const b of base) map.set(b.id, b);

    for (const d of domains) {
      const key = (d.key || '').trim();
      if (!key) continue;
      const existing = map.get(key);
      map.set(key, { id: key, label: (d.label || existing?.label || key).trim() });
    }

    const baseOrder = base.map((b) => b.id);
    const baseItems = baseOrder.map((id) => map.get(id)).filter(Boolean) as { id: string; label: string }[];
    const extraItems = Array.from(map.values()).filter((x) => !baseOrder.includes(x.id));
    extraItems.sort((a, b) => a.label.localeCompare(b.label));
    // Area color: the configured color, else the built-in palette's (General's for an unknown key).
    return [...baseItems, ...extraItems].map((o) => ({
      ...o,
      color: o.id === 'all' ? '' : getDomainMeta(o.id, domains).color,
    }));
  }, [domains]);

  // The floating "+" follows the selected area: its ink, its name, and a new item's area.
  // All areas, or a lens that is not a listed area, keeps the plain "+".
  const quickAddArea = useMemo(
    () => (filterDomain && filterDomain !== 'all'
      ? contextOptions.find((o) => o.id === filterDomain) ?? null
      : null),
    [filterDomain, contextOptions],
  );

  const activeModeLabel = useMemo(() => {
    if (filterDomain === null || filterDomain === 'all') return null;
    const override = MODE_LABEL_OVERRIDES[filterDomain];
    if (override) return override;
    const match = contextOptions.find((ctx) => ctx.id === filterDomain);
    return match?.label || filterDomain;
  }, [filterDomain, contextOptions]);

  const decisionsNeeded = useMemo(() => {
    return omitTodayFamilyMembers(items).filter(i =>
      (i.meta as any)?.decision_needed === true
      && i.state !== 'DONE'
      && i.state !== 'ARCHIVED'
      && i.attention !== 'watch'
      && i.attention !== 'ignore'
    );
  }, [items]);

  // Keyboard order matches the Today board: column order now → next → triage → waiting (row-major on 2×2 and 1×4 grids).
  // Decision Hub mirrors some items but is not in j/k order; board cards carry `data-today-card-item-id` for scroll/focus.
  const flattenedItems = useMemo(
    () => [...bucketed.now, ...bucketed.next, ...bucketed.triage, ...bucketed.waiting],
    [bucketed]
  );

  const updateItem = async (id: string, patch: UpdatePatch) => {
    const updated = await workItemsApi.updateWorkItem(id, patch);
    setItems(prev => prev.map(i => (i.id === id ? { ...i, ...updated } : i)));
    loadCalendar();
  };

  const markDone = async (id: string) => {
    const target = items.find((candidate) => candidate.id === id);
    if (target && isSourceHomeItem(target)) return;
    await updateItem(id, { state: 'DONE', needs_action: false });
  };

  const archiveItem = async (id: string) => {
    await updateItem(id, { state: 'ARCHIVED', needs_action: false });
  };

  const markWatchItemsArchived = (ids: Set<string>) => {
    if (ids.size === 0) return;
    setItems(prev => prev.map(item =>
      ids.has(item.id)
        ? { ...item, state: 'ARCHIVED', needs_action: false }
        : item
    ));
  };

  const archiveWatchStack = async (stack: WatchStack) => {
    const ids = stack.items.map(item => item.id);
    // One bulk call per (attention, state) shown in the stack: a held ACTIVE item, or an
    // undecided high whose attention is not watch, sits beside the TRIAGE watch rows.
    const groups = new Map<string, { attention: Attention; state: string; ids: string[] }>();
    for (const item of stack.items) {
      const attention: Attention = item.attention || 'watch';
      const state = normalize(item.state) || 'TRIAGE';
      const key = `${attention}|${state}`;
      const group = groups.get(key) || { attention, state, ids: [] };
      group.ids.push(item.id);
      groups.set(key, group);
    }
    setArchivingWatchStacks(prev => new Set(prev).add(stack.key));
    setWatchStackError(null);
    const updatedIds = new Set<string>();
    try {
      for (const group of Array.from(groups.values())) {
        for (let offset = 0; offset < group.ids.length; offset += BULK_ARCHIVE_LIMIT) {
          const chunk = group.ids.slice(offset, offset + BULK_ARCHIVE_LIMIT);
          const result = await workItemsApi.bulkUpdateWorkItems(
            chunk,
            { state: 'ARCHIVED', needs_action: false },
            { attention: group.attention, state: group.state },
          );
          const chunkUpdated = new Set(result.updated_ids || []);
          chunkUpdated.forEach(id => updatedIds.add(id));
          markWatchItemsArchived(chunkUpdated);
        }
      }
      loadCalendar();
      if (updatedIds.size < ids.length) {
        const remaining = ids.length - updatedIds.size;
        setWatchStackError(`Could not archive ${remaining} ${stack.label} update${remaining === 1 ? '' : 's'}. Try again.`);
        return;
      }
      setExpandedWatchStacks(prev => {
        const next = new Set(prev);
        next.delete(stack.key);
        return next;
      });
    } catch {
      markWatchItemsArchived(updatedIds);
      if (updatedIds.size > 0) {
        loadCalendar();
      }
      const remaining = ids.length - updatedIds.size;
      setWatchStackError(
        remaining > 0 && remaining < ids.length
          ? `Could not archive ${remaining} ${stack.label} update${remaining === 1 ? '' : 's'}. Try again.`
          : `Could not archive ${stack.label} updates. Try again.`,
      );
    } finally {
      setArchivingWatchStacks(prev => {
        const next = new Set(prev);
        next.delete(stack.key);
        return next;
      });
    }
  };

  const snoozeItem = async (id: string, days: number) => {
    const d = addDays(new Date(), days);
    await updateItem(id, { snoozed_until: d.toISOString() });
  };

  const startItem = async (id: string) => {
    await updateItem(id, { state: 'ACTIVE', needs_action: true });
  };

  const setFocusId = useCallback((id?: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) {
      next.set('focusId', id);
    } else {
      next.delete('focusId');
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const openDrawer = useCallback((item: WorkItem | null, activator?: HTMLElement | null) => {
    drawerSelectionRef.current += 1;
    if (activator) {
      drawerActivatorRef.current = activator;
    } else if (item?.id) {
      const board = document.querySelector('[data-testid="today-board"]');
      drawerActivatorRef.current = board?.querySelector(
        `[data-today-card-item-id="${CSS.escape(item.id)}"]`,
      ) as HTMLElement | null;
    } else {
      drawerActivatorRef.current = null;
    }
    setDrawerItem(item);
    setDrawerOpen(true);
    if (item?.id) {
      setFocusId(item.id);
      try {
        sessionStorage.setItem(LAST_FOCUS_ID_KEY, item.id);
        sessionStorage.setItem(LAST_FOCUS_ITEM_KEY, JSON.stringify(item));
      } catch {
        // Ignore storage failures.
      }
    }
  }, [setFocusId]);

  const openDueItem = useCallback(
    async (workItemId: string, activator: HTMLElement) => {
      const fromBoard = items.find((candidate) => candidate.id === workItemId);
      if (fromBoard) {
        openDrawer(fromBoard, activator);
        return;
      }
      const selection = ++drawerSelectionRef.current;
      try {
        const item = await workItemsApi.getWorkItem(workItemId);
        if (selection !== drawerSelectionRef.current) return;
        openDrawer(item, activator);
      } catch {
        // Missing or not owned — leave the chip; do not invent a drawer.
      }
    },
    [items, openDrawer],
  );

  // Drawer prev/next + auto-advance walk the board in column order (Now → Next → Triage → Waiting).
  const todayPileLabel = useMemo(
    () => todayBoardPileLabel({
      now: bucketed.now.length,
      next: bucketed.next.length,
      triage: bucketed.triage.length,
      later: bucketed.waiting.length,
    }),
    [bucketed],
  );

  const drawerNavigation = useDrawerNavigation(
    flattenedItems,
    drawerOpen ? drawerItem?.id ?? null : null,
    (id: string) => {
      const target = flattenedItems.find((candidate) => candidate.id === id);
      if (!target) return false;
      openDrawer(target);
      return true;
    },
  );
  const drawerNavigationWithPile = useMemo(
    () => (drawerNavigation ? { ...drawerNavigation, pileLabel: todayPileLabel } : undefined),
    [drawerNavigation, todayPileLabel],
  );

  const closeDrawer = () => {
    drawerSelectionRef.current += 1;
    setDrawerOpen(false);
    setDrawerItem(null);
    setFocusId(null);
    try {
      sessionStorage.removeItem(LAST_FOCUS_ID_KEY);
      sessionStorage.removeItem(LAST_FOCUS_ITEM_KEY);
    } catch {
      // Ignore storage failures.
    }
    const el = drawerActivatorRef.current;
    drawerActivatorRef.current = null;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (el && document.contains(el)) {
          el.focus({ preventScroll: true });
        } else {
          const main = document.querySelector('main');
          if (main instanceof HTMLElement) {
            main.focus({ preventScroll: true });
          }
        }
      });
    });
  };

  const handleDrawerUpdate = (updated: WorkItem, sourceRule?: AppliedSourceRule) => {
    setItems(prev => {
      const patched = patchWorkItemsForAppliedSourceRule(prev, updated, sourceRule);
      const visible = patched.filter((item) => (item.state || '').toUpperCase() !== 'ARCHIVED');
      const exists = visible.some(i => i.id === updated.id);
      if (exists) {
        return visible;
      }
      if ((updated.state || '').toUpperCase() === 'ARCHIVED') {
        return visible;
      }
      return [updated, ...visible];
    });
    // The drawer may already be on another item (auto-advance); only refresh it for a match.
    setDrawerItem((prev) => (prev && prev.id === updated.id ? updated : prev));
    if (drawerItem?.id === updated.id) {
      setFocusId(updated.id);
      try {
        sessionStorage.setItem(LAST_FOCUS_ID_KEY, updated.id);
        sessionStorage.setItem(LAST_FOCUS_ITEM_KEY, JSON.stringify(updated));
      } catch {
        // Ignore storage failures.
      }
    }
    loadCalendar();
  };

  const handleDrawerDelete = (deletedId: string) => {
    const removed = drawerItem?.id === deletedId ? drawerItem : null;
    drawerSelectionRef.current += 1;
    setItems(prev => releaseLocalFamilyOnSourceHomeDelete(prev, deletedId, removed));
    setDrawerItem(null);
    loadCalendar();
  };

  useEffect(() => {
    if (drawerOpen || drawerItem || focusIdFromUrl) return;
    try {
      const raw = sessionStorage.getItem(LAST_FOCUS_ITEM_KEY);
      if (!raw) return;
      const cached = JSON.parse(raw) as WorkItem | null;
      if (cached?.id) {
        setDrawerItem(cached);
        setDrawerOpen(true);
        setFocusId(cached.id);
      }
    } catch {
      // Ignore storage failures or bad JSON.
    }
  }, [drawerOpen, drawerItem, focusIdFromUrl, setFocusId]);

  useEffect(() => {
    if (!drawerItem?.id || items.length === 0) return;
    const match = items.find((item) => item.id === drawerItem.id);
    if (match && match !== drawerItem) {
      setDrawerItem(match);
    }
  }, [items, drawerItem]);

  useEffect(() => {
    if (loading) return;
    if (!focusIdFromUrl) {
      deepLinkAppliedRef.current = null;
      try {
        const lastFocusId = sessionStorage.getItem(LAST_FOCUS_ID_KEY);
        if (lastFocusId && deepLinkAppliedRef.current !== lastFocusId) {
          const match = items.find((item) => item.id === lastFocusId);
          if (match) {
            deepLinkAppliedRef.current = lastFocusId;
            openDrawer(match);
          }
        }
      } catch {
        // Ignore storage failures.
      }
      return;
    }
    if (deepLinkAppliedRef.current === focusIdFromUrl) return;

    const match = items.find((item) => item.id === focusIdFromUrl);
    if (!match) return;

    deepLinkAppliedRef.current = focusIdFromUrl;
    openDrawer(match);
  }, [focusIdFromUrl, items, loading, openDrawer]);

  const {
    focusedId,
    selectedIds,
    setSelectedIds,
    handleItemClick,
    handleCheckboxClick,
    focusSource,
    setFocusSource,
  } = useTriageNavigation({
    items: flattenedItems,
    onOpen: openDrawer,
    onDone: async (ids: string[]) => {
      for (const id of ids) await markDone(id);
    },
    onArchive: async (ids: string[]) => {
      for (const id of ids) await archiveItem(id);
    },
    onSnooze: async (ids: string[]) => {
      for (const id of ids) await snoozeItem(id, 1);
    },
    shortcutsEnabled: !drawerOpen,
  });

  useEffect(() => {
    if (!focusedId || focusSource !== 'keyboard') return;

    const bucketKeys: BucketKey[] = ['now', 'next', 'triage', 'waiting'];
    const bucketForId = bucketKeys.find((k) => bucketed[k].some((i) => i.id === focusedId)) ?? null;
    if (bucketForId) {
      const idx = bucketed[bucketForId].findIndex((i) => i.id === focusedId);
      if (idx >= MAX_VISIBLE_PER_BUCKET && !expanded[bucketForId]) {
        setExpanded((prev) => ({ ...prev, [bucketForId]: true }));
        return;
      }
    }

    const board = document.querySelector('[data-testid="today-board"]');
    const el = board?.querySelector(
      `[data-today-card-item-id="${CSS.escape(focusedId)}"]`,
    ) as HTMLElement | null;
    if (!el) {
      setFocusSource(null);
      return;
    }
    // Match Workbench: no smooth scroll during rapid j/k (also respects reduced-motion expectations, UX-DR25).
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    el.focus({ preventScroll: true });
    setFocusSource(null);
  }, [focusedId, focusSource, setFocusSource, bucketed, expanded]);

  const clearSelection = () => setSelectedIds(new Set());

  const toggleExpanded = (bucket: BucketKey) => {
    setExpanded(prev => ({ ...prev, [bucket]: !prev[bucket] }));
  };

  const toggleWatchStack = useCallback((sourceKey: string) => {
    setExpandedWatchStacks(prev => {
      const next = new Set(prev);
      if (next.has(sourceKey)) {
        next.delete(sourceKey);
      } else {
        next.add(sourceKey);
      }
      return next;
    });
  }, []);

  const handleCardClick = (item: WorkItem) => (e: React.MouseEvent) => {
    if (e.shiftKey || e.metaKey || e.ctrlKey) {
      handleItemClick(item.id, e);
      return;
    }
    handleItemClick(item.id, e);
    openDrawer(item, e.currentTarget as HTMLElement);
  };

  const handleInlineAction = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const snoozeOneDay = (id: string) => {
    void snoozeItem(id, 1);
  };

  const [isBriefingCollapsed, setIsBriefingCollapsed] = useState(false);
  const [watchStreamsExpanded, setWatchStreamsExpanded] = useState(() => {
    try {
      return sessionStorage.getItem(WATCH_STREAMS_EXPANDED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleWatchStreamsExpanded = () => {
    setWatchStreamsExpanded(prev => {
      const next = !prev;
      try {
        sessionStorage.setItem(WATCH_STREAMS_EXPANDED_KEY, next ? '1' : '0');
      } catch {
        /* ignore quota / private mode */
      }
      return next;
    });
  };

  const AIBriefingHeader = () => {
    if (items.length === 0) return null;

    const nowCount = bucketed.now.length;
    const nextCount = bucketed.next.length;
    const triageCount = bucketed.triage.length;
    const focusItem = bucketed.triage[0] || bucketed.now[0];

    const content = (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-4 text-xs font-medium text-theme-secondary">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-red-400"></span>
            <span className="text-theme font-bold">{nowCount}</span>
            <span>Priority Now</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-amber-400"></span>
            <span className="text-theme font-bold">{nextCount}</span>
            <span>Up Next</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-blue-400"></span>
            <span className="text-theme font-bold">{triageCount}</span>
            <span>Triage</span>
          </div>
          {focusItem?.title && (
            <>
              <span className="text-theme-muted">•</span>
              <span className="text-theme-muted">First:</span>
              <span className="text-chip-indigo">{focusItem.title}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => focusItem && openDrawer(focusItem)}
            className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-[11px] font-bold transition-all shadow-lg shadow-indigo-500/20"
          >
            Review Triage
          </button>
          <button
            onClick={() => setIsBriefingCollapsed(!isBriefingCollapsed)}
            className="p-1.5 text-theme-muted hover:text-theme-secondary transition-colors"
            aria-label={isBriefingCollapsed ? "Expand briefing" : "Collapse briefing"}
          >
            <FontAwesomeIcon icon={isBriefingCollapsed ? faChevronDown : faChevronUp} className="text-[10px]" />
          </button>
        </div>
      </div>
    );

    if (isBriefingCollapsed) {
      return (
        <div className="mb-6 p-3 rounded-2xl glass-card">
          {content}
        </div>
      );
    }

    return (
      <div className="mb-6 p-4 rounded-2xl bg-theme-secondary border border-theme shadow-lg">
        {content}
      </div>
    );
  };

  /*
  const ContextSwitcher = () => {
    const contexts = [
      { id: 'all', label: 'Life View', icon: '🌐' },
      { id: 'job_hunt', label: 'Work Mode', icon: '💼' },
      { id: 'finance', label: 'Finance', icon: '💰' },
      { id: 'parenthood', label: 'Home Mode', icon: '🧸' },
      { id: 'business', label: 'Business', icon: '🏢' },
    ];

    return (
      <div className="flex items-center gap-2 mb-6 bg-white/[0.03] p-1.5 rounded-2xl border border-white/[0.05] w-fit">
        {contexts.map(ctx => (
          <button
            key={ctx.id}
            onClick={() => setFilterDomain(ctx.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${filterDomain === ctx.id
              ? 'bg-white/10 text-white border border-white/10'
              : 'text-white/40 hover:text-white/70'
              }`}
          >
            <span>{ctx.icon}</span>
            {ctx.label}
          </button>
        ))}
      </div>
    );
  };
  */

  const DecisionHubRow = () => {
    if (decisionsNeeded.length === 0) return null;

    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-4 px-1">
          <h3 className="text-theme font-semibold text-lg">Decision Hub</h3>
          <span className="text-[10px] bg-red-500/10 text-danger px-2 py-0.5 rounded-full font-bold uppercase tracking-tighter">Action Required</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {decisionsNeeded.map(item => {
            const styling = getWorkItemCardClasses(item, domains, workItemStylingOptions);
            return (
              <div
                key={item.id}
                tabIndex={-1}
                onClick={handleCardClick(item)}
                className={`p-4 cursor-pointer group relative rounded-2xl bg-red-500/[0.03] border-2 border-red-500/20 hover:bg-red-500/[0.05] hover:border-red-500/40 transition-all motion-reduce:transition-none overflow-hidden ${styling.domain.leftBorderClass}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{styling.domain.icon}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-danger">Decision Needed</span>
                  </div>
                  <span className="flex h-2 w-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)] animate-pulse"></span>
                </div>

                <h4 className="text-theme font-medium text-sm mb-1 group-hover:text-danger transition-colors line-clamp-1">{item.title}</h4>
                <p className="text-theme-secondary text-[11px] line-clamp-2 leading-relaxed">{item.summary}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const BulkActionsBar = () => {
    if (selectedIds.size === 0) return null;
    const ids = Array.from(selectedIds);
    return (
      <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60] animate-in fade-in slide-in-from-top-4 duration-300">
        <div className="bg-theme-secondary border border-theme rounded-full px-5 py-2.5 shadow-2xl flex items-center gap-3">
          <span className="text-xs text-theme font-bold mr-2 uppercase tracking-widest">{selectedIds.size} SELECTED</span>
          <div className="flex items-center gap-1">
            <Tooltip title="Mark Done">
              <IconButton size="small" sx={{ color: 'var(--color-text-primary)' }} onClick={async () => { for (const id of ids) await markDone(id); clearSelection(); }}>
                <FontAwesomeIcon icon={faCheckCircle} className="text-chip-success" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Snooze +1 day">
              <IconButton size="small" sx={{ color: 'var(--color-text-primary)' }} onClick={async () => { for (const id of ids) await snoozeItem(id, 1); clearSelection(); }}>
                <FontAwesomeIcon icon={faClock} className="text-chip-warning" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Archive">
              <IconButton size="small" sx={{ color: 'var(--color-text-primary)' }} onClick={async () => { for (const id of ids) await archiveItem(id); clearSelection(); }}>
                <FontAwesomeIcon icon={faArchive} className="text-theme-secondary" />
              </IconButton>
            </Tooltip>
            <div className="w-px h-4 bg-theme-muted/20 mx-1"></div>
            <Tooltip title="Clear selection">
              <IconButton size="small" sx={{ color: 'var(--color-text-muted)' }} onClick={clearSelection}>
                <FontAwesomeIcon icon={faTimesCircle} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  };

  const QuickAddFAB = () => {
    const area = quickAddArea;
    return (
      <button
        type="button"
        aria-label={area ? `New item in ${area.label}` : 'New item'}
        data-testid="WB_TODAY_QUICK_ADD"
        data-area-id={area?.id}
        onClick={(e) => openDrawer(null, e.currentTarget)}
        className={`today-quick-add fixed right-8 h-14 w-14 rounded-full shadow-2xl flex items-center justify-center transition-all motion-reduce:transition-none hover:scale-110 motion-reduce:hover:scale-100 active:scale-95 motion-reduce:active:scale-100 z-50 group border border-theme ${area ? 'today-area-fill' : 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-indigo-500/40'}`}
        style={area ? inkVars(area) : undefined}
      >
        <FontAwesomeIcon
          icon={faPlus}
          className="text-xl group-hover:rotate-90 transition-transform duration-300 motion-reduce:transition-none motion-reduce:group-hover:rotate-0"
        />
      </button>
    );
  };

  const columnShared: Pick<
    TodayColumnProps,
    | 'domains'
    | 'workItemStylingOptions'
    | 'todayYmd'
    | 'focusedId'
    | 'openDrawerId'
    | 'selectedIds'
    | 'onCardClick'
    | 'onInlineAction'
    | 'onCheckboxClick'
    | 'onOpenDrawer'
    | 'onStart'
    | 'onDone'
    | 'onSnooze'
    | 'onArchive'
  > = {
    domains,
    workItemStylingOptions,
    todayYmd,
    focusedId,
    openDrawerId: drawerOpen ? drawerItem?.id ?? null : null,
    selectedIds,
    onCardClick: handleCardClick,
    onInlineAction: handleInlineAction,
    onCheckboxClick: handleCheckboxClick,
    onOpenDrawer: (item: WorkItem, activator?: HTMLElement | null) => openDrawer(item, activator),
    onStart: startItem,
    onDone: markDone,
    onSnooze: snoozeOneDay,
    onArchive: archiveItem,
  };

  const syncButton = (
    <button
      type="button"
      data-testid="today-sync-button"
      data-sync-state={syncState.kind}
      onClick={() => openConnectGmailModal()}
      aria-label={syncState.ariaLabel}
      className={`shrink-0 h-11 lg:h-10 px-4 rounded-xl text-[13px] font-semibold whitespace-nowrap transition-all flex items-center gap-2 ${SYNC_BUTTON_TONE_CLASSES[syncState.tone]}`}
      style={syncState.tone === 'neutral' ? { backgroundColor: 'var(--color-bg-tertiary)' } : undefined}
    >
      <FontAwesomeIcon icon={SYNC_BUTTON_ICONS[syncState.kind]} aria-hidden />
      {syncState.label}
    </button>
  );

  return (
    <PageContainer
      className="selection:bg-indigo-500/30"
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        backgroundImage: 'radial-gradient(circle at 50% 0%, var(--color-accent, rgba(79, 70, 229, 0.05)) 0%, transparent 50%)'
      } as any}
    >
      <header className="flex flex-col md:flex-row md:flex-wrap md:items-center justify-between gap-4 mb-6 glass-card p-4 rounded-[2rem]">
        <div className="flex flex-wrap lg:flex-nowrap items-center gap-4 w-full lg:w-auto justify-between lg:justify-start min-w-0">
          <div className="w-full lg:w-auto min-w-0">
            <div className="flex items-center justify-between gap-3">
              <h1 className="text-3xl font-bold text-theme tracking-tight">Today</h1>
              {!isDesktop && syncButton}
            </div>
            {isDesktop ? (
              <>
                <p className="text-xs text-theme-muted mt-0.5">{headerDateLine}</p>
                {userFirstName && (
                  <p className="text-sm text-theme-secondary mt-1">
                    Hi, {userFirstName}
                  </p>
                )}
              </>
            ) : (
              <p data-testid="today-date-line" className="text-sm text-theme-secondary mt-1">
                {userFirstName ? `${headerDateLine} · Hi, ${userFirstName}` : headerDateLine}
              </p>
            )}
            {activeModeLabel && (
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest rounded-full border border-indigo-500/30 bg-indigo-500/10 text-chip-indigo">
                  {activeModeLabel}
                </span>
              </div>
            )}
          </div>

          <div className="h-10 w-px border-theme hidden lg:block mx-2" style={{ backgroundColor: 'var(--color-border)' }}></div>

          {/* Area row: equal cells and More below 1024 px, one compact row above (today-phone-area-row) */}
          <TodayAreaRow
            options={contextOptions}
            selectedId={filterDomain}
            onSelect={setFilterDomain}
            isDesktop={isDesktop}
            extra={hasSessionLensOverride ? (
              <button
                type="button"
                onClick={followProfileDomainLens}
                className="ml-1 shrink-0 text-[10px] md:text-[11px] font-semibold uppercase tracking-tight px-2 py-1 rounded-lg border border-theme text-theme-secondary hover:text-theme hover:bg-theme-card-hover whitespace-nowrap"
              >
                Use profile mode
              </button>
            ) : null}
          />
        </div>

        {/* Below 1024 px Refresh is cut and the Color legend lives in the menu (today-phone-header). */}
        <div className="hidden lg:flex lg:flex-nowrap items-center gap-3">
          {/* Minimal AI Status Bar */}
          {filterDomain !== null && !workItemsLoading && items.length > 0 && (
            <div
              onClick={() => setIsBriefingCollapsed(!isBriefingCollapsed)}
              className="hidden lg:flex items-center gap-3 px-4 py-2 rounded-xl bg-purple-500/5 border border-purple-500/10 hover:bg-purple-500/10 cursor-pointer transition-all"
            >
              <div className="flex items-center gap-2 text-[11px] font-bold">
                <span className="text-chip-violet">{bucketed.now.length}</span>
                <span className="text-theme-secondary uppercase tracking-tighter">Urgent</span>
                <span className="text-theme-muted mx-1">/</span>
                <span className="text-chip-violet">{decisionsNeeded.length}</span>
                <span className="text-theme-secondary uppercase tracking-tighter">Decisions</span>
              </div>
              <FontAwesomeIcon icon={isBriefingCollapsed ? faChevronDown : faChevronUp} className="text-[10px] text-theme-muted" />
            </div>
          )}

          <div className="flex items-center gap-2 p-1.5 md:p-1 rounded-2xl md:rounded-xl" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}>
            <Tooltip title="Refresh">
              <button
                type="button"
                onClick={refreshToday}
                className="h-11 w-11 md:h-8 md:w-8 flex items-center justify-center rounded-xl md:rounded-lg hover:bg-indigo-500/10 transition-all text-lg md:text-base"
                style={{ color: 'var(--color-text-muted)' }}
                aria-label="Refresh"
              >
                <FontAwesomeIcon icon={faRedo} />
              </button>
            </Tooltip>
            <Tooltip title="Color Legend">
              <button
                type="button"
                onClick={() => setLegendOpen(true)}
                className="h-11 w-11 md:h-8 md:w-8 flex items-center justify-center rounded-xl md:rounded-lg hover:bg-indigo-500/10 transition-all text-lg md:text-base"
                style={{ color: 'var(--color-text-muted)' }}
                aria-label="Open color legend"
              >
                <FontAwesomeIcon icon={faPalette} />
              </button>
            </Tooltip>
          </div>

          {isDesktop && syncButton}
        </div>
      </header>

          {error && (
        <div className="mb-8 rounded-2xl border border-red-500/20 bg-red-500/5 p-4 text-danger text-xs font-medium flex items-center gap-3">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse"></span>
          {plainTodayFetchError(error)}
        </div>
      )}

      {domainBootstrapPending ? (
        <div className="flex flex-col items-center justify-center py-32 space-y-4">
          <div className="h-12 w-12 rounded-2xl border-2 border-indigo-500/20 border-t-indigo-500 animate-spin"></div>
          <div className="text-theme-muted text-xs font-bold uppercase tracking-widest">Establishing Control...</div>
        </div>
      ) : (
        <>
          {/* Calendar state is GET /today/calendar. google_connected only drives the mail chip. */}
          <TodayCalendarStrip
            state={calendar?.state ?? 'loading'}
            events={calendar?.events ?? []}
            timeZone={tzEffective}
            onOpenDue={openDueItem}
          />
          <div className="sr-only" aria-live="polite">
            {boardLoadAnnouncement}
          </div>
          {!workItemsLoading && (
            <>
              {!isBriefingCollapsed && <AIBriefingHeader />}
              <DecisionHubRow />
              <TodayWatchStacks
                watchStacks={watchStacks}
                watchStreamsExpanded={watchStreamsExpanded}
                onToggleWatchStreams={toggleWatchStreamsExpanded}
                watchStackError={watchStackError}
                expandedWatchStacks={expandedWatchStacks}
                archivingWatchStacks={archivingWatchStacks}
                onToggleStack={toggleWatchStack}
                onArchiveStack={archiveWatchStack}
                onOpenItem={(item: WorkItem, activator?: HTMLElement | null) => openDrawer(item, activator)}
                openDrawerId={drawerOpen ? drawerItem?.id ?? null : null}
              />
            </>
          )}

          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-3"
            data-testid="today-board"
            role="region"
            aria-label="Today priority board"
            aria-busy={workItemsLoading}
          >
            <TodayColumn
              bucket="now"
              title="Priority Now"
              subtitle="0–1 Days · urgent or due very soon"
              items={bucketed.now}
              wipLimit={5}
              isLoading={workItemsLoading}
              isExpanded={expanded.now}
              onToggleExpanded={() => toggleExpanded('now')}
              emptySlot={
                <BucketEmptyContent
                  bucket="now"
                  gmailConnected={gmailConnected}
                  mailNeedsReconnect={mailGrantNeedsReconnect}
                  onAddWork={() => openDrawer(null)}
                  onRefresh={refresh}
                  onConnectMail={() => openConnectGmailModal()}
                  onOpenWorkbench={() => navigate('/app/workbench')}
                />
              }
              {...columnShared}
            />
            <TodayColumn
              bucket="next"
              title="Up Next"
              subtitle="2–7 Days · upcoming window"
              items={bucketed.next}
              isLoading={workItemsLoading}
              isExpanded={expanded.next}
              onToggleExpanded={() => toggleExpanded('next')}
              emptySlot={
                <BucketEmptyContent
                  bucket="next"
                  gmailConnected={gmailConnected}
                  mailNeedsReconnect={mailGrantNeedsReconnect}
                  onAddWork={() => openDrawer(null)}
                  onRefresh={refresh}
                  onConnectMail={() => openConnectGmailModal()}
                  onOpenWorkbench={() => navigate('/app/workbench')}
                />
              }
              {...columnShared}
            />
            <TodayColumn
              bucket="triage"
              title="Triage"
              subtitle="Needs triage · new or unclassified"
              items={bucketed.triage}
              isLoading={workItemsLoading}
              isExpanded={expanded.triage}
              onToggleExpanded={() => toggleExpanded('triage')}
              emptySlot={
                <BucketEmptyContent
                  bucket="triage"
                  gmailConnected={gmailConnected}
                  mailNeedsReconnect={mailGrantNeedsReconnect}
                  onAddWork={() => openDrawer(null)}
                  onRefresh={refresh}
                  onConnectMail={() => openConnectGmailModal()}
                  onOpenWorkbench={() => navigate('/app/workbench')}
                />
              }
              {...columnShared}
            />
            <TodayColumn
              bucket="waiting"
              title="Later"
              subtitle="Deferred · 8+ days or not yet urgent"
              items={bucketed.waiting}
              isLoading={workItemsLoading}
              isExpanded={expanded.waiting}
              onToggleExpanded={() => toggleExpanded('waiting')}
              emptySlot={
                <BucketEmptyContent
                  bucket="waiting"
                  gmailConnected={gmailConnected}
                  mailNeedsReconnect={mailGrantNeedsReconnect}
                  onAddWork={() => openDrawer(null)}
                  onRefresh={refresh}
                  onConnectMail={() => openConnectGmailModal()}
                  onOpenWorkbench={() => navigate('/app/workbench')}
                />
              }
              {...columnShared}
            />
          </div>

          <BulkActionsBar />
          <QuickAddFAB />
          {!isDesktop && (
            // Room at the end of Today so the last card's buttons scroll clear of the "+".
            <div aria-hidden="true" data-testid="today-quick-add-clearance" className="today-quick-add-clearance" />
          )}
        </>
      )}

      <WorkItemDrawer
        open={drawerOpen}
        item={drawerItem}
        newItemDomain={quickAddArea?.id}
        onClose={closeDrawer}
        onUpdate={handleDrawerUpdate}
        onDelete={handleDrawerDelete}
        navigation={drawerNavigationWithPile}
        onOpenFamilyMember={(member) => openDrawer(member)}
      />
      <ColorLegend open={legendOpen} onClose={closeLegend} domains={domains} />
    </PageContainer>
  );
};

export default TodayPage;
